import {
  useNotificarSaidaDonoVideochamadaMutation,
  useNotificarVideochamadaLotadaMutation,
  useResponderSolicitacaoEntradaVideochamadaMutation,
} from 'graphql/hooks.generated'
import { RespostaSolicitacaoEntradaVideochamadaEnum } from 'graphql/types.generated'
import useAtmosphere from 'hooks/useAtmosphere'
import { useLogger } from 'hooks/useLogger'
import { useOnBeforeUnload } from 'hooks/useOnBeforeUnload'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  SolicitacaoEntradaVideochamadaAtmosphereResponse,
  StatusSolicitacaoEntradaVideochamadaEnum,
} from '../model-videochamada'
import { SolicitacaoEntrarVideochamadaConfirmModal } from './SolicitacaoEntrarVideochamadaConfirmModal'
import { SolicitacaoEntrarVideochamadaTimeoutModal } from './SolicitacaoEntrarVideochamadaTimeoutModal'

const SOLICITACAO_TIMEOUT_LIMIT = 5 * 60 * 1000 // milisec
const MAX_PARTICIPANTES = 2

interface SolicitacaoEntradaVideochamada {
  nomeSolicitante: string
  status: StatusSolicitacaoEntradaVideochamadaEnum
  participanteId: ID
}

interface SolicitacaoEntrarVideochamadaListenerProps {
  videochamadaUuid: ID
  numeroParticipantes: number
}

export function SolicitacaoEntrarVideochamadaListener(props: SolicitacaoEntrarVideochamadaListenerProps) {
  const { videochamadaUuid, numeroParticipantes } = props
  const logger = useLogger()

  const [solicitacaoEntrada, setSolicitacaoEntrada] = useState<SolicitacaoEntradaVideochamada>()
  const isAguardandoResposta =
    solicitacaoEntrada?.status === StatusSolicitacaoEntradaVideochamadaEnum.AGUARDANDO_RESPOSTA
  const timeout = useRef<NodeJS.Timeout>()
  const videochamadaLotada = useMemo(() => numeroParticipantes + Number(isAguardandoResposta) >= MAX_PARTICIPANTES, [
    isAguardandoResposta,
    numeroParticipantes,
  ])

  const [notificarSaidaDonoVideochamada] = useNotificarSaidaDonoVideochamadaMutation()
  const [notificarVideochamadaLotada] = useNotificarVideochamadaLotadaMutation()

  useEffect(() => {
    notificarVideochamadaLotada({
      variables: {
        videochamadaUuid,
        lotada: videochamadaLotada,
      },
    })
  }, [notificarVideochamadaLotada, videochamadaLotada, videochamadaUuid])

  const [responderEntradaVideochamada] = useResponderSolicitacaoEntradaVideochamadaMutation()

  const handleSolicitacao = useCallback(
    (response: SolicitacaoEntradaVideochamadaAtmosphereResponse) => {
      if (response?.cancelar && solicitacaoEntrada) {
        if (solicitacaoEntrada.participanteId === response.participanteId) setSolicitacaoEntrada(null)
      }
      if (videochamadaLotada) {
        responderEntradaVideochamada({
          variables: {
            input: {
              videochamadaUuid,
              participanteId: response.participanteId,
              resposta: RespostaSolicitacaoEntradaVideochamadaEnum.VIDEOCHAMADA_LOTADA,
            },
          },
        })
      } else {
        const { participanteId, nomeParticipante } = response

        setSolicitacaoEntrada({
          nomeSolicitante: nomeParticipante,
          status: StatusSolicitacaoEntradaVideochamadaEnum.AGUARDANDO_RESPOSTA,
          participanteId,
        })

        timeout.current = setTimeout(() => {
          setSolicitacaoEntrada({
            nomeSolicitante: nomeParticipante,
            status: StatusSolicitacaoEntradaVideochamadaEnum.TIMEOUT,
            participanteId,
          })

          responderEntradaVideochamada({
            variables: {
              input: {
                videochamadaUuid,
                participanteId,
                resposta: RespostaSolicitacaoEntradaVideochamadaEnum.TIMEOUT,
              },
            },
          })
        }, SOLICITACAO_TIMEOUT_LIMIT)
      }
    },
    [videochamadaLotada, responderEntradaVideochamada, solicitacaoEntrada, videochamadaUuid]
  )

  useAtmosphere<SolicitacaoEntradaVideochamadaAtmosphereResponse>({
    topic: `public/videochamada/${videochamadaUuid}/solicitacoesEntrada`,
    onMessage: handleSolicitacao,
    onConnect: () => logger.info('Tópico de entrada na videochamada conectado'),
  })

  const responderSolicitacao = useCallback(
    async (aceita: boolean, keepalive = false) => {
      logger.info(`${aceita ? 'Permitindo' : 'Negando'} entrada na videochamada`)
      timeout.current && clearTimeout(timeout.current)
      timeout.current = null

      if (solicitacaoEntrada)
        await responderEntradaVideochamada({
          variables: {
            input: {
              videochamadaUuid,
              participanteId: solicitacaoEntrada.participanteId,
              resposta: aceita
                ? RespostaSolicitacaoEntradaVideochamadaEnum.ACEITA
                : RespostaSolicitacaoEntradaVideochamadaEnum.NEGADA,
            },
          },
          context: {
            fetchOptions: {
              keepalive: keepalive,
            },
          },
        })
      setSolicitacaoEntrada(null)
    },
    [responderEntradaVideochamada, solicitacaoEntrada, videochamadaUuid, logger]
  )

  const handleClose = () => responderSolicitacao(false)
  const handleBeforeUnload = useCallback(() => {
    responderSolicitacao(false, true)
    notificarSaidaDonoVideochamada({
      variables: {
        videochamadaUuid,
      },
      context: {
        fetchOptions: {
          keepAlive: true,
        },
      },
    })
  }, [notificarSaidaDonoVideochamada, responderSolicitacao, videochamadaUuid])

  useOnBeforeUnload(handleBeforeUnload)

  switch (solicitacaoEntrada?.status) {
    case StatusSolicitacaoEntradaVideochamadaEnum.AGUARDANDO_RESPOSTA:
      return (
        <SolicitacaoEntrarVideochamadaConfirmModal
          nomeSolicitante={solicitacaoEntrada.nomeSolicitante}
          onResponse={responderSolicitacao}
          onClose={handleClose}
        />
      )
    case StatusSolicitacaoEntradaVideochamadaEnum.TIMEOUT:
      return (
        <SolicitacaoEntrarVideochamadaTimeoutModal
          nomeSolicitante={solicitacaoEntrada.nomeSolicitante}
          timeoutLimit={SOLICITACAO_TIMEOUT_LIMIT}
          onClose={handleClose}
        />
      )
    default:
      return null
  }
}
